import { type ClassValue, clsx } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

// The semantic type scale (text-display, text-h1…h5, text-2xs) and the
// `shadow-float` step are custom theme values, so tailwind-merge doesn't know
// they belong to the font-size / shadow groups. Without registering them a
// later `text-sm` in a className wouldn't override `text-h1` — or worse, a
// heading class would be dropped when merged. Register them here; any new
// scale step must be added to this list too (DESIGN_SYSTEM.md §3).
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      "font-size": [
        { text: ["display", "h1", "h2", "h3", "h4", "h5", "2xs"] },
      ],
      shadow: [{ shadow: ["float"] }],
    },
  },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Compact "time ago" formatter for list/card metadata (e.g. "Updated 3d ago").
// Returns "just now" under a minute and falls back to a locale date past a week,
// so cards stay scannable without pulling in a date library. Invalid/missing
// input returns "" so callers can omit the line cleanly.
export function formatRelativeTime(input: string | number | Date): string {
  const date = new Date(input);
  const ms = date.getTime();
  if (Number.isNaN(ms)) return "";

  const seconds = Math.round((Date.now() - ms) / 1000);
  if (seconds < 45) return "just now";

  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;

  // Older than a week: an absolute date is clearer than "5w ago".
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
